<script>
    import {formatUnits, parseUnits} from "ethers/lib/utils";
    import Button from "../button/Button.svelte";
    import Steps from "../steps/Steps.svelte";
    import {BLOCK_EXPLORER} from "../../constants/constants";
    import Ring from "../ring/Ring.svelte";
    import {BigNumber, ethers} from "ethers";
    import Input from "../input/Input.svelte";
    import {isTransactionConfirmed, projectSaleDetails, tokenPurchased} from "../../store";
    import {getTokenPrice} from "../../utils";

    const TxStatus = {
        None: "None",
        AwaitingSignature: "AwaitingSignature",
        AwaitingConfirmation: "AwaitingConfirmation",
        Error : "Error",
    }

    const BuySteps = {
        Confirm: "Confirm",
        Complete: "Complete",
    }

    const PriceConfirmed = {
        Pending : "Pending",
        Confirmed : "Confirmed",
    }

    const inputValueStatuses = {
        VALID: "",
        NEGATIVE_ERROR: "Only Positive values allowed",
        EXCEED_LIMIT: "Entered amount exceeds buy limit"
    }

    const tokenDecimals = 1000000000000000000

    export let signer, sale, saleData;

    let units,
        fee,
        activeStep = BuySteps.Confirm,
        txStatus = TxStatus.None,
        priceConfirmed = PriceConfirmed.Pending,
        txReceipt,
        errorMsg,
        calcPricePromise, inputValueStatus,
        isWalletCapExceeded = false

    const calculatePrice = async (amount) => {
        isWalletCapExceeded = ((amount + $tokenPurchased) <= formatUnits(saleData.data.sales[0].calculatePriceStateConfig.constants[1])) ? false : true;
        priceConfirmed = PriceConfirmed.Pending;
        const _units = parseUnits(
            amount.toString(),
            saleData.data.sales[0].token.decimals.toString()
        );
        units = _units;
        const price = await sale.calculatePrice(_units);
        const total = price.mul(_units);
        priceConfirmed = PriceConfirmed.Confirmed;

        return {total}
    };

    const buy = async () => {
        const buyConfig = {
            feeRecipient: $signer._address, // should be set to platform fee recipient
            fee: BigNumber.from("0"),
            minimumUnits: units,
            desiredUnits: units,
            maximumPrice: ethers.constants.MaxUint256,
        };
        let tx;
        txStatus = TxStatus.AwaitingSignature;

        try {
            tx = await sale.buy(buyConfig);
        } catch (error) {
            errorMsg = error.data?.message || error?.message;
            txStatus = TxStatus.Error;
            return;
        }

        txStatus = TxStatus.AwaitingConfirmation;
        txReceipt = await tx.wait();
        txStatus = TxStatus.None;
        activeStep = BuySteps.Complete;
        setTimeout(() => {
            isTransactionConfirmed.set(true);
        }, 5000);
    };
    const isValidInput = (value) => {
        if(value <= 0) {
            inputValueStatus = "NEGATIVE_ERROR";
            return false;
        }
        if (value > Number((+formatUnits($projectSaleDetails.unitsAvailable, $projectSaleDetails.token.decimals)).toFixed(0))) {
            inputValueStatus = "EXCEED_LIMIT"
            return false;
        }
        inputValueStatus = "VALID"
        return true
    }
</script>

{#if txStatus === TxStatus.None}
    <div class="flex column-direction buy-modal">
        <Steps steps={["Confirm", "Complete"]} {activeStep} fulfilledTextClass="text-gray-100" lineBorderClass="border-gray-400"/>
        {#if activeStep === BuySteps.Confirm}
            <h1 class="modal-title">Buy {saleData.data.sales[0].token.symbol}</h1>
            <Input type="number" debounce on:input={({ detail }) => {
                calcPricePromise = isValidInput(detail) && calculatePrice(detail);
            }}>
                <span class="title" slot="label">Enter the number of units to buy:</span>
            </Input>
            <div class="info-wrapper">
                {#if inputValueStatus && inputValueStatus !== "VALID"}
                    <span class="warning">*{inputValueStatuses[inputValueStatus]}</span>
                {/if}
            </div>
            {#if calcPricePromise}
                <div class="white info-wrapper flex column-direction">
                    {#await calcPricePromise}
                        Getting price...
                    {:then result}
                        {#if !isWalletCapExceeded && inputValueStatus === "VALID"}
                        <div class="flex flex-wrap history">
                            <span>Price: {getTokenPrice(saleData.data.sales[0])} {saleData.data.sales[0].reserve.symbol} per {saleData.data.sales[0].token.symbol}</span>
                            <span class="ml-auto">Total: {formatUnits(result.total, saleData.data.sales[0].reserve.decimals)/tokenDecimals} {saleData.data.sales[0].reserve.symbol}</span>
                        </div>
                            <Button buttonClass={"submit-btn "} disabled={!priceConfirmed} on:click={buy}>Buy {saleData.data.sales[0].token.symbol}</Button>
                        {:else}
                            <span class="warning">*Wallet Cap breached</span>
                            {/if}
                    {/await}
                </div>
            {/if}
        {/if}
        {#if activeStep === BuySteps.Complete && inputValueStatus}
            <span class="modal-title">Purchase confirmed!</span>
            <a
                class="text-blue-400 underline"
                target="_blank"
                href={`${BLOCK_EXPLORER}/tx/${txReceipt?.transactionHash}`}>See transaction.</a>
        {/if}
    </div>
{/if}

{#if txStatus === TxStatus.AwaitingSignature}
    <div class="flex items-center">
        <Ring color="#fff" />
        <span class="text-lg">Awaiting signature...</span>
    </div>
{/if}
{#if txStatus === TxStatus.AwaitingConfirmation}
    <div class="flex items-center">
        <Ring color="#fff" />
        <span class="text-lg">Transaction confirming...</span>
    </div>
{/if}
{#if txStatus === TxStatus.Error}
    <div class="flex column-direction buy-modal items-center">
        <span class="modal-title">Something went wrong.</span>
        <span class="text-lg text-red-400">{errorMsg}</span>
    </div>
{/if}
<style>
    span.text-lg{
        color: #fff;
    }
    .buy-modal span.title{
        font-size: 18px;
        line-height: 22px;
        color: #fff;
        margin: 0 0 12px;
        display: block;
    }
    .info-wrapper span{
        color: #fff;
        font-size: 16px;
        line-height: 22px;
        margin: 0 0 15px;
        font-weight: 300;
    }
    .info-wrapper .warning{
        color: red;
        font-weight: bold;
    }
    .white{
        color: #fff;
    }
</style>
